import { apiRequest } from "@api/configs/axios.config";
import { CACHE_KEYS } from "@api/constants/cacheKeys";
import URLS from "@api/constants/urls";
import { handleGlobalGetRequestQuery } from "@api/functions/getRequests";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { IGetServiceProps } from "@api/types/globalService.types";

export interface DownloadParams {
  type: "policy" | "product";
  typeId: number;
  paymentModeId: number;
}

const useDownloadCertificateCancellationFormatService = () => {
  const [params, setParams] = useState<DownloadParams | null>(null);

  const triggerCertificateFormatDownload = (newParams: DownloadParams) => {
    setParams(newParams);
  };

  const downloadCertificateCancellationFormatQuery = useQuery({
    queryKey: [
      CACHE_KEYS.CERTIFICATE_CANCELLATION_FORMAT,
      params?.type,
      params?.typeId,
      params?.paymentModeId,
    ],
    queryFn: () => {
      if (!params) return;
      const { type, typeId, paymentModeId } = params;
      const url = `${
        type === "policy" ? URLS.POLICIES : URLS.PRODUCTS
      }/${typeId}/certificates/format/download?payment_mode_id=${paymentModeId}&upload_excel_for=bulk_cancellation`;
      return apiRequest.get(url);
    },
    enabled: !!params,
  });

  return {
    downloadCertificateCancellationFormatQuery,
    triggerCertificateFormatDownload,
  };
};

const useDownloadCertificateDeletionFormatService = () => {
  const downloadCertificateDeletionFormatQuery = useQuery({
    queryKey: [CACHE_KEYS.CERTIFICATE_DELETION_FORMAT],
    queryFn: () => {
      const url = `upload${URLS.CERTIFICATES}/format/download?upload_excel_for=bulk_cancellation`;
      return apiRequest.get(url);
    },
  });

  return {
    downloadCertificateDeletionFormatQuery,
  };
};

const useGetBulkCertificateCancelLogs = ({
  page = "1",
  per_page = "1",
}: IGetServiceProps) => {
  const allBulkUploadCertificateCancelLogs = useQuery({
    queryKey: [...CACHE_KEYS.BULK_CERTIFICATE_CANCEL_LOGS, page, per_page],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: URLS.BULK_CERTIFICATE_CANCEL_LOGS,
        searchParams: {
          page,
          per_page,
        },
      }),
  });

  return {
    services: { allBulkUploadCertificateCancelLogs },
  };
};

const useGetBulkCertificateDeleteLogs = ({
  page = "1",
  per_page = "1",
}: IGetServiceProps) => {
  const allBulkUploadCertificateDeleteLogs = useQuery({
    queryKey: [...CACHE_KEYS.BULK_CERTIFICATE_DELETE_LOGS, page, per_page],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: URLS.BULK_CERTIFICATE_DELETE_LOGS,
        searchParams: {
          page,
          per_page,
        },
      }),
  });

  return {
    services: { allBulkUploadCertificateDeleteLogs },
  };
};

export {
  useDownloadCertificateCancellationFormatService,
  useGetBulkCertificateCancelLogs,
  useDownloadCertificateDeletionFormatService,
  useGetBulkCertificateDeleteLogs,
};
